import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { StateService } from '@uirouter/core';

import { StoreAction } from 'store/actions';
import { Accessibility, CimaCore, Cloudinary, Formatter, Scroll, Util, XmStore, XmStoreUtil } from 'core/services';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { PhonePipe } from 'shared/pipes/phone';

import { Datasaver } from 'store/line/models';
import { CmsActivity, CmsAddonDetails, CmsDataDetail, CmsLineDataPlanCollection, CmsMyAccount, CmsVideoResolution } from 'store/cms/models';
import { LineBannerInfo } from 'store/line-banner/models';
import { CustomersDetails, ProfileLines, User } from 'store/user/models';
import { LineUsage, UsageSummary } from 'store/usage/models';
import { AccountSummary, LineSummary, PlanSummary, SummaryLineWithPlan } from 'store/account-summary/models';
import { Addon } from 'store/addons/addon';
import { ActivityOptions, AddonType, BannerAction, DataPlanCodes, LineStatus } from 'my-account/constants';
import { BannerService } from 'my-account/services/banner';
import { LineTransferBySessionId } from 'store/line/models/line-transfer/line-transfer-by-session-id';
import { IXMOptions } from 'core/interfaces';
import { AccountType, CONFIG_TOKEN, PlanSevenUnlPremiumPlanIds } from 'core/constants';
import { VideoResolutionInfo } from 'store/video-resolution/models';
import { catchError } from 'rxjs/operators';
import { GlobalTravelPass, GlobalTravelPassLine, GlobalTravelPassTab, LineDetailsAddOnContent } from 'store/global-travel-pass/models';
import { BillingBannerInfo } from 'store/billing-banner/models';

interface DataOptionPlan {
    activePlan: boolean;
    plan: PlanSummary;
    planData: CmsDataPlanDetails;
    isActiveLine: boolean;
}

@Component({
    selector: 'line-detail',
    styleUrls: ['./detail.scss'],
    templateUrl: './detail.html',
    standalone: false
})
export class LineDetailComponent implements OnInit, OnDestroy {
    public currentUsage: LineUsage;
    public usages: LineUsage[] = [];
    public currentCycleDuration: string;
    public imageOptions: MediaImageOptions;
    public bannerSubHeader: string;
    public bannerDescription: string;
    public accountNo: string;
    public bannerButtonText: string;
    public graphData: GraphItem[];
    public displayUsage: string;
    public showPastUsage: boolean = false;
    public pastUsageLabel: string = 'Show Past Usage';
    public shouldShowBanner: boolean = false;
    public currentBanner: CmsActivityBannerTemplate;
    public domesticUsageCost: number;
    public pageData: CmsDataDetail;
    public dataPlanPageData: CmsDataPlanDetails[];
    public currentLine: LineSummary;
    public currentPlan: PlanSummary;
    public planName: string;
    public byTheGigPlanName: string;
    public unlimitedPlanName: string;
    public watchStaticImage: MediaImageOptions;
    public pairedLines: PairedLineLite[] = [];
    public cmsAddonDetails: CmsAddonDetails;
    public dataOptionPlans: DataOptionPlan[] = [];
    public disclaimerMessage: string;

    public isDatasaverEligible: boolean;
    public isStatusOn: boolean;
    public isDefaultAddon: boolean;
    public accountSummary: AccountSummary;
    public isLegacyOrder: boolean;
    public apiRequestData: StoreFetchInfo;

    public addons: Addon[] = [];
    public showAddonsSection: boolean;

    public addonsWarningMessage: string = '';
    public dataOptionsWarningMessage: string = '';
    public displayName: string;
    public lineStatus: string;
    public user: User;
    public userRole: string = '';
    public buttonAttributes: ButtonAttributes;
    public image5G: MediaImageOptions;
    public showLevelMove: boolean;
    public isLineTransferPending: boolean;
    public isToggleEligibility: boolean = false;
    public isBusinessUser: boolean;
    public videoResolutionInfo: VideoResolutionInfo;
    public cmsVideoResolution: CmsVideoResolution;
    public isLoading: boolean = true;
    public isLineBannerLoading: boolean = true;

    public isMember: boolean = false;
    public isViewer: boolean = false;
    // Call and Message Log Activities
    public call: string = ActivityOptions.CALLS;
    public messages: string = ActivityOptions.MESSAGES;

    // Feature flag
    public enablePlanSixMvePlus: boolean = false;
    public enableFccBroadbandLabel: boolean = false;
    public isGrandFatheredLabel: boolean = false;
    public isSegmentFive: boolean = false;
    public isSegmentSix: boolean = false;
    public isSeg6ResiUser: boolean = false;
    public isEnableLineLevelExp: boolean = false;
    public isLLEEnabledMemberOrViewer: boolean = false;
    public isIcopPlanSwitch: boolean = false;
    public newAddons: LineDetailsAddOnContent[] = [];
    public showUserCta: boolean = false;
    public processedUserCtaText: string = '';
    public profileLines: ProfileLines;
    public promotionsBanner: BillingBanner;
    public promoList: BannerPromos[] = [];
    public enableLineLevelExperienceCBM: boolean = false;
    public enableCbmNoLevel: boolean = false;

    private xmStore: XmStore;
    private subscriptions: Subscription[] = [];
    private state: StateService;
    private cmsActivityData: CmsActivity;
    private bannerInfo: LineBannerInfo;
    private phonePipe: PhonePipe;
    private scroll: Scroll;
    private accessibility: Accessibility;
    private bannerService: BannerService;
    private config: IXMOptions;
    private cimaCore: CimaCore;

    constructor(accessibility: Accessibility, state: StateService, xmStore: XmStore, phonePipe: PhonePipe, scroll: Scroll, bannerService: BannerService, cimaCore: CimaCore, @Inject(CONFIG_TOKEN) config: IXMOptions) {
        Object.assign(this, { accessibility, state, xmStore, phonePipe, scroll, bannerService, cimaCore, config });
    }

    public ngOnInit(): void {
        this.isBusinessUser = this.cimaCore.isBusinessUser;
        this.isIcopPlanSwitch = this.config.ENABLE_ICOP_P1_PLANSWITCH;
        this.enableLineLevelExperienceCBM = this.config.ENABLE_CBM_LINE_LEVEL_EXP && this.cimaCore.isBusinessUser;

        // get data detail page CMS
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsDataDetail>(CmsDataDetail), (pageData: CmsDataDetail) => {
            this.pageData = pageData;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsLineDataPlanCollection>(CmsLineDataPlanCollection), (result: CmsLineDataPlanCollection) => {
            this.dataPlanPageData = result.plans;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsMyAccount>(CmsMyAccount), (cmsMyAccount: CmsMyAccount) => {
            this.byTheGigPlanName = cmsMyAccount.plans.byTheGig;
            this.unlimitedPlanName = cmsMyAccount.plans.unlimited;
        }));

        // get addons detail page CMS
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsAddonDetails>(CmsAddonDetails), (result: CmsAddonDetails) => {
            this.cmsAddonDetails = result;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<AccountSummary>(AccountSummary), (accountSummary: AccountSummary) => {
            this.accountSummary = accountSummary;
            if (this.accountSummary) {
                const lineByKey: SummaryLineWithPlan = this.accountSummary.getLineSummaryWithPlanByKey(this.state.params.line_id);
                let currentPlanName: string = '';
                this.currentLine = lineByKey.line;
                this.currentPlan = lineByKey.plan;

                this.buildWarningMessage();
                this.buildDisclaimerMessage();
                this.displayUsage = Util.mbToGb(this.currentLine.dataUsage);
                this.lineStatus = this.getLineStatus();

                if (this.currentLine.pairedLines) {
                    this.currentLine.pairedLines.forEach((line: PairedLine) => {
                        const linewithPlan: SummaryLineWithPlan = this.accountSummary.getLineSummaryWithPlanByKey(line.lineId.key);

                        this.pairedLines.push({
                            id: line.lineId,
                            name: linewithPlan.line.displayName,
                            planPrice: `$${linewithPlan.plan.planPrice}/mo`
                        });
                    });
                }
                this.image5G = {
                    mobile: Cloudinary.generateMediaFromCms(this.pageData.levelMoveInfo.icon)
                };

                // Watch static image for Paired Device section
                this.watchStaticImage = {
                    mobile: Cloudinary.generateMediaFromCms(this.pageData.pairedDevice.watchImage)
                };

                // Display Plan Name based on Plan Type
                if (this.currentPlan.isTablet) {
                    this.planName = this.pageData.dataUsage.tabletUsageLabel;
                } else {
                    currentPlanName = this.currentPlan.isUnlimited ? this.unlimitedPlanName : this.byTheGigPlanName;
                    this.planName = this.pageData.dataUsage.currentPlanName.replace('[PLAN_NAME]', currentPlanName);
                }

                // Display Line Name or Device Make and Model for Active Device
                this.displayName = this.currentLine.displayName || (this.currentLine.isActive && this.currentLine.makeAndModel);

                this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<User>(User), (user: User) => {
                    this.accountNo = user.accountNo ? this.pageData.accountNo.replace('[ACCOUNT_NO]', user.accountNo) : '';
                    this.user = user;
                    this.showLevelMove = user.account.canMoveLevel;
                    this.isSegmentFive = user.account.isSegmentFive;
                    this.isSegmentSix = user.account.isSegmentSix;
                    this.isSeg6ResiUser = !this.isBusinessUser && user.account.isSegmentSix;
                    this.enableCbmNoLevel = this.config.ENABLE_CBM_GEMINI && this.isBusinessUser && user.account.isNoLevel;
                    this.enableFccBroadbandLabel = (this.user.account.isSegmentFive) || (this.user.account.isSegmentSix);
                    this.isGrandFatheredLabel = user.account.level === AccountType.LEVEL_2 || user.account.level === AccountType.LEVEL_3 || user.account.level === AccountType.LEVEL_4;
                    this.setPlanOptions();
                    this.userRole = user.account.role;
                    this.isMember = user.account.isMember;
                    this.isViewer = user.account.isViewer;
                    this.showUserCta = user.account.isPrimary && (!this.isBusinessUser || this.enableLineLevelExperienceCBM);
                    this.isLLEEnabledMemberOrViewer = (this.isMember || this.isViewer);

                    if (this.enableLineLevelExperienceCBM) {
                        this.pageData.cbmAssignUserCta = this.pageData.cbmAssignUserCta?.replace('[CBM_PAYMENT_BASE_URL]', this.config.CBM_PAYMENT_BASE_URL);
                        this.processedUserCtaText = this.pageData.cbmAssignUserCta || '';
                    } else {
                        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<ProfileLines>(ProfileLines, {
                        }), (profileLines: ProfileLines) => {
                            this.profileLines = profileLines;
                            this.handleLineIdCases();
                        }));
                    }
                }));
            }

            if (window.location.hash) {
                this.accessibility.focusHash(window.location.hash);
            }

            // API Failure
            let apiAction: string;

            if (this.config.ENABLE_CBM_GEMINI && this.isBusinessUser && this.user?.account?.isNoLevel) {
                apiAction = StoreAction.GET_SSM_ACCOUNT_SUMMARY_CBM_NO_LEVEL;
            } else if (this.user?.account?.isSegmentSix && !this.isBusinessUser) {
                apiAction = StoreAction.GET_ACCOUNT_SUMMARY_FOR_PLAN_EIGHT;
            } else if (this.isSegmentFive || this.isSeg6ResiUser) {
                apiAction = StoreAction.GET_ACCOUNT_SUMMARY_VIA_SSM_PROXY;
            } else {
                apiAction = StoreAction.GET_ACCOUNT_SUMMARY;
            }

            this.apiRequestData = {
                apiAction
            };
        }));

        this.setDefaultAddonsData();

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<Datasaver>(Datasaver, { filters: { lineKey: this.state.params.line_id } }), (response: Datasaver) => {
            this.isDatasaverEligible = response.isEligible;
            this.isStatusOn = response.isStatusOn;
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<LineTransferBySessionId>(LineTransferBySessionId), (response: LineTransferBySessionId) => {
            this.isLineTransferPending = response.getPendingLineByValue(this.currentLine.id.value) || response.getPendingLineByValue(this.currentLine.id.key);
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsActivity>(CmsActivity), (cmsActivityData: CmsActivity) => {
            this.cmsActivityData = cmsActivityData;
        }));

        this.xmStore.findPromise<LineBannerInfo>(StoreAction.GET_LINE_BANNER_INFO, LineBannerInfo, {
            params: { lineId: this.state.params.line_id },
            filters: { lineId: this.state.params.line_id }
        }).then((bannerInfo: LineBannerInfo) => {
            this.bannerInfo = bannerInfo;
            this.addNextCycleStartDateToBannerInfo();
            this.currentBanner = this.cmsActivityData.getCurrentLineBanner(this.bannerInfo);
            if (this.currentBanner) {
                this.bannerSubHeader = this.cmsActivityData.getLineBannerSubHeader(this.currentBanner, this.bannerInfo);
                this.bannerDescription = this.cmsActivityData.getLineBannerDescription(this.currentBanner, this.bannerInfo);
                
                if (this.bannerInfo?.ctaButton?.roamingCharge) {
                    this.bannerDescription = this.bannerDescription.replace('[ROAMING_CHARGE]', this.bannerInfo.ctaButton.roamingCharge);
                }
                
                this.bannerButtonText = this.currentBanner.ctaButton ? this.currentBanner.ctaButton.label : '';
                if (this.currentBanner.ctaButton) {
                    this.buttonAttributes = this.bannerService.setBannerButtonAction(this.currentBanner, this.bannerInfo);
                }

                this.imageOptions = {
                    mobile: {
                        base: this.cmsActivityData.getBannerImageUrl(this.currentBanner),
                        alt: this.currentBanner.image?.alt,
                        useBoundingBox: true,
                        trimTransparency: true
                    }
                };

                this.shouldShowBanner = this.user.account.isPrimary || this.user.account.isManager;
                this.isLineBannerLoading = false;
            }
        });

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<UsageSummary>(UsageSummary, {
            filters: { 'lineId.key': this.state.params.line_id }
        }), (usageSummary: UsageSummary) => {
            this.currentUsage = usageSummary.currentUsage;
            this.usages = usageSummary.usageItems;
            this.domesticUsageCost = this.currentUsage.domesticDataCost;
            this.currentCycleDuration = Formatter.formatCycleDuration(this.currentUsage.cycleStartDate, this.currentUsage.cycleEndDate, true);

            if (this.usages.length) {
                this.graphData = this.usages.map<GraphItem>((usage: LineUsage) => ({
                    current: false,
                    selected: false,
                    value: usage.domesticDataConsumed,
                    date: new Date(usage.cycleStartDate)
                }));
            }
        }));

        if (this.isIcopPlanSwitch) {
            this.subscriptions.push(XmStoreUtil.subscribe(
                this.xmStore.find<BillingBannerInfo>(StoreAction.GET_BILLING_BANNER, BillingBannerInfo), (bannerInfo: BillingBannerInfo) => {
                    if (bannerInfo && bannerInfo?.banners) {
                        this.promotionsBanner = bannerInfo.btgLineDetailsBanner;
                        this.promoList = this.promotionsBanner?.promotionContent?.promos?.filter((promo: BannerPromos) =>
                            promo?.associatedLineIds?.includes(this.currentLine.id.value) || promo?.associatedLineIds?.includes(this.currentLine.id.key)
                        );
                    }
                }
            ));
        }

        if (this.currentLine.isVideoResolution) {
            this.getVideoResolutionAndAddons();
        } else {
            this.getAddons();
        }
    }

    public getVideoResolutionAndAddons(): void {
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.find<CmsVideoResolution>(StoreAction.GET_CMS_VIDEO_RESOLUTION, CmsVideoResolution),
            (cmsVideoResolution: CmsVideoResolution) => {
                this.cmsVideoResolution = cmsVideoResolution;
            }
        ));

        // If isEnableLineLevelExp and if its a Member or Viewer role, don't have to call video resolution API
        if (this.isEnableLineLevelExp && (this.isMember || this.isViewer) && !this.enableCbmNoLevel) {
            const gtpAction: StoreAction = this.isSeg6ResiUser ? StoreAction.GET_GLOBAL_TRAVEL_PASS_PLAN_EIGHT : StoreAction.GET_GLOBAL_TRAVEL_PASS;
            this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.find<GlobalTravelPass>(gtpAction, GlobalTravelPass), (addons: GlobalTravelPass) => {
                this.buildNewAddonData(addons);
                this.isLoading = false;
            }, () => {
                this.errorHandlerForAddonsAndVideoResolution();
            }));
        } else {
            const $videoResolutionInfo: Observable<VideoResolutionInfo> = this.xmStore.find<VideoResolutionInfo>(StoreAction.GET_VIDEO_RESOLUTION, VideoResolutionInfo, {
                params: { lineId: this.state.params.line_id },
                filters: { lineId: this.state.params.line_id }
            }).pipe(
                catchError(() => of(undefined))
            );

            const $newAddonCollection: Observable<GlobalTravelPass> = this.enableCbmNoLevel
                ? of(undefined)
                : this.xmStore.find<GlobalTravelPass>(
                    this.isSeg6ResiUser ? StoreAction.GET_GLOBAL_TRAVEL_PASS_PLAN_EIGHT : StoreAction.GET_GLOBAL_TRAVEL_PASS, GlobalTravelPass
                ).pipe(
                    catchError(() => of(undefined))
                );

            this.subscriptions.push(combineLatest([$videoResolutionInfo, $newAddonCollection]).subscribe(
                ([videoInfo, addons]: [VideoResolutionInfo, GlobalTravelPass]) => {
                    if (videoInfo) {
                        this.videoResolutionInfo = videoInfo;
                        this.isToggleEligibility = videoInfo.isVideoToggleEligible &&
                            (this.user.account.isSegmentFour || this.isSegmentFive || this.isSegmentSix) &&
                            this.currentLine.isVideoResolution &&
                            (this.currentPlan.isTablet || this.currentPlan.isUnlimited);
                    }
                    if (addons) {
                        this.buildNewAddonData(addons);
                    }
                    this.isLoading = false;
                }, () => {
                    this.errorHandlerForAddonsAndVideoResolution();
                }
            ));
        }
    }

    public getAddons(): void {
        if (!this.enableCbmNoLevel) {
            this.subscriptions.push(XmStoreUtil.subscribe(
                this.xmStore.find<GlobalTravelPass>(
                    this.isSeg6ResiUser ? StoreAction.GET_GLOBAL_TRAVEL_PASS_PLAN_EIGHT : StoreAction.GET_GLOBAL_TRAVEL_PASS, GlobalTravelPass
                ),
                (gtp: GlobalTravelPass) => {
                    this.isLoading = false;
                    this.buildNewAddonData(gtp);
                }
            ));
        } else {
            this.isLoading = false;
        }
    }

    public errorHandlerForAddonsAndVideoResolution(): void {
        this.setDefaultAddonsData();
        this.isLoading = false;
    }

    public togglePastUSage(): void {
        this.showPastUsage = !this.showPastUsage;
        this.pastUsageLabel = this.showPastUsage ? 'Hide Past Usage' : 'Show Past Usage';
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    public goToBannerCtaAction(): void {
        switch (this.currentBanner.ctaButton.action) {
            case BannerAction.SCROLL:
                this.scroll.toHash('data-options');
                break;
        }
    }

    public setDefaultAddonsData(): void {
        const isActiveLine: boolean = this.currentLine && this.currentLine.isActive;
        this.isDefaultAddon = Boolean(this.addons.length === 0) && isActiveLine;
    }

    public levelMoveUp(): void {
        this.state.go('account.settings.level-move.landing');
    }

    public displayUsageLink(usageLogNumber: number): boolean {
        return usageLogNumber > 0;
    }

    public showXmCallGuard(): boolean {
        return PlanSevenUnlPremiumPlanIds.includes(this.currentLine.planId);
    }

    private buildNewAddonData(gtp: GlobalTravelPass): void {
        gtp.tabs.forEach((tab: GlobalTravelPassTab) => {
            const foundLine: GlobalTravelPassLine = tab.lines.find((line: GlobalTravelPassLine) => (
                line.lineIdKey === this.currentLine.id.key
            ));
            if (foundLine && foundLine?.status) {
                this.newAddons.push({
                    lineId: this.currentLine.id.key,
                    status: foundLine.status,
                    removalRequestCode: foundLine?.removalRequestCode ?? '',
                    type: this.getAddonTypeByPass(tab.pass),
                    ...foundLine.lineDetails
                });
            }
        });
        this.showAddonsSection = Boolean(this.newAddons.length);
    }

    private getAddonTypeByPass(pass: string): AddonType {
        if (pass === 'gtp') {
            return AddonType.GTP;
        } else if (pass === 'international') {
            return AddonType.INTERNATIONAL;
        } else {
            return AddonType.CAN_MEX_UNL_PASS;
        }
    }

    private buildWarningMessage(): void {
        if (this.currentLine.isDeactivated) {
            this.addonsWarningMessage = this.pageData.warningMessage.deactivatedLine.addons;
            this.dataOptionsWarningMessage = this.pageData.warningMessage.deactivatedLine.dataOptions;
        } else if (this.currentLine.isSuspended) {
            this.addonsWarningMessage = this.pageData.warningMessage.suspendLine.addons;
            this.dataOptionsWarningMessage = this.pageData.warningMessage.suspendLine.dataOptions;
        } else if (this.currentLine.isPending) {
            this.dataOptionsWarningMessage = this.pageData.warningMessage.pendingLine.dataOptions;
        }
    }

    private buildDisclaimerMessage(): void {
        if (this.currentLine.isActive) {
            const planContent: CmsDataPlanDetails = this.getContentByPlanCode(this.currentLine.planCode);
            this.disclaimerMessage = planContent?.disclaimer;
        }
    }

    private getContentByPlanCode(code: string): CmsDataPlanDetails {
        return this.dataPlanPageData.find((data: CmsDataPlanDetails) => data.planCode.includes(code));
    }

    private getContentByBucketId(bucketId: string): CmsDataPlanDetails {
        return this.dataPlanPageData.find((data: CmsDataPlanDetails) => data.bucketId?.includes(bucketId));
    }

    private getBTGSharedOrDefaultContent(): CmsDataPlanDetails {
        return this.accountSummary.hasSharedPlan ? this.getContentByBucketId(this.accountSummary.bucketId) : this.getContentByPlanCode(DataPlanCodes.BY_THE_GIG_DEFAULT);
    }

    private setPlanOptions(): void {
        if ((this.currentLine.isTablet && this.accountSummary.level5Customer) || this.currentLine.isStandaloneWatch) {
            this.dataOptionPlans.push({
                activePlan: true,
                plan: this.currentPlan,
                planData: this.getContentByPlanCode(this.currentLine.planCode),
                isActiveLine: this.currentLine.isActive
            });
        } else {
            this.dataOptionPlans.push({
                activePlan: true,
                plan: this.currentPlan,
                planData: this.getContentByPlanCode(this.currentLine.planCode),
                isActiveLine: this.currentLine.isActive
            }, {
                activePlan: false,
                plan: this.currentPlan.isUnlimited ? this.accountSummary.hasSharedPlan && this.accountSummary.sharedPlan : undefined,
                planData: this.buildPlanData(),
                isActiveLine: this.currentLine.isActive
            });
        }
    }

    private buildPlanData(): CmsDataPlanDetails {
        if (this.currentPlan.isByTheGigShared) {
            return this.user.account.isSegmentTwo ? this.getContentByPlanCode(DataPlanCodes.UNLIMITED_LEVEL2_PHONE)
                : this.currentLine.isTablet ? this.getContentByPlanCode(DataPlanCodes.UNLIMITED_LEVEL3_TABLET) : this.getContentByPlanCode(DataPlanCodes.UNLIMITED_LEVEL3_PHONE_1_LINE);
        } else {
            return this.getBTGSharedOrDefaultContent();
        }
    }

    private getLineStatus(): string {
        switch (this.currentLine.status) {
            case LineStatus.ACTIVE:
                return this.phonePipe.transform(this.currentLine.lineNumber);
            case LineStatus.PENDING:
                return this.pageData.lineStatus.notActivated;
            case LineStatus.SUSPENDED:
                return this.pageData.lineStatus.suspended;
            case LineStatus.DEACTIVATED:
                return this.pageData.lineStatus.cancelled;
        }
    }

    private addNextCycleStartDateToBannerInfo(): void {
        if (!this.bannerInfo.subheader.nextCycleStartMonth || !this.bannerInfo.subheader.nextCycleStartDate) {
            const nextCycleStartDate: Date = new Date(this.accountSummary.cycleEndDate);
            nextCycleStartDate.setDate(nextCycleStartDate.getDate() + 1);
            this.bannerInfo.subheader.nextCycleStartMonth = nextCycleStartDate.toLocaleString('en-US', { month: 'short' });
            this.bannerInfo.subheader.nextCycleStartDate = nextCycleStartDate.toLocaleString('en-US', { day: 'numeric' });
        }
    }

    private handleLineIdCases(): void {
        const currentLineId: string = this.state.params.line_id;

        // Case 1: Line in unassociated lines → Go to general settings  
        if (this.isLineUnassociated(currentLineId)) {
            this.processUserCmsText();

            return;
        }

        // Case 2: Line assigned to Member/Viewer → Show manage user CTA
        const memberViewerCustomer: CustomersDetails | undefined = this.findLineInMemberViewerCustomers(currentLineId);
        if (memberViewerCustomer) {
            this.processUserCmsText(memberViewerCustomer.custGuid);

            return;
        }

        // Case 3 & 4: Line assigned to Primary/Manager OR not found → Go to general settings
        this.processUserCmsText();
    }

    private isLineUnassociated(lineId: string): boolean {
        return Boolean(this.profileLines?.unassociatedLines?.some(
            (line: UnassociatedLine) => line.lineIdKey === lineId
        ));
    }

    private findLineInMemberViewerCustomers(lineId: string): CustomersDetails | undefined {
        return this.profileLines?.memberOrViewerCustomers?.find((customer: CustomersDetails) =>
            customer.lines?.some((line: CustomerLines) => line.lineIdKey === lineId)
        );
    }

    private processUserCmsText(custGuid?: string): void {
        if (custGuid) {
            // Case 2: Manage user CTA for Member/Viewer lines
            this.processedUserCtaText = this.pageData.manageThisUserCta?.replace('[CUSTOMER_BASE_URL]', this.config.CUSTOMER_BASE_URL)
                .replace('{CUST_GUID}', custGuid)
                .replace(/\[\$([^|]+)\|([^$]+)\$\]/g, '[$$$$1|$$2$$]');
        } else {
            // Case 1, 3 & 4: Assign user CTA for unassociated or Primary/Manager lines
            this.pageData.assignUserCta = this.pageData.assignUserCta?.replace('[CUSTOMER_BASE_URL]', this.config.CUSTOMER_BASE_URL);
            this.processedUserCtaText = this.pageData.assignUserCta || '';
        }
    }

}
